import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import identityApi from '../clients/IdentityClient';
import { type User } from '../models/User';
import { CreateNewUserRequestBody } from '../models/requestBodies/CreateNewUserRequestBody';
import { SetPasswordRequestBody } from '../models/requestBodies/SetPasswordRequestBody';
import { useNavigate } from 'react-router-dom';
import { currentTenant } from '../tenant/tenants';

interface AuthContextType {
  user: User | null;
  balance: number | null;
  login: (email: string, password: string) => Promise<boolean>;
  signUp: (signUpData: Omit<User, 'id' | 'loginName'>) => Promise<{ success: boolean, error?: string }>;
  logout: () => void;
  refreshUserProfile: () => Promise<boolean>;
  refreshBalance: () => Promise<void>;
  resetPassword: (email: string) => Promise<boolean>;
  setPassword: (payload: SetPasswordRequestBody) => Promise<boolean>;
  isLoading: boolean;
  isSessionExpired: boolean;
  closeSessionExpiredDialog: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [balance, setBalance] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const savedUser = localStorage.getItem('driverUser');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (error) {
        localStorage.removeItem('driverUser');
      }
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    const handleUnauthorized = () => {
      logout();
      setIsSessionExpired(true);
      navigate('/login');
    };

    window.addEventListener('auth:unauthorized', handleUnauthorized);

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, [navigate]);

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      const token = await identityApi.login({ userName: email, password });
      if (!token) {
        return false;
      }

      return await refreshUserProfile();
    }
    catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const signUp = async (signUpData: Omit<User, 'id' | 'loginName'>): Promise<{ success: boolean, error?: string }> => {
    try {
      const createUserPayload: CreateNewUserRequestBody = {
        email: signUpData.email,
        firstName: signUpData.firstName!,
        lastName: signUpData.lastName!,
        dateOfBirth: signUpData.dateOfBirth,
        phone: signUpData.phone
      };

      const response = await identityApi.createUser(createUserPayload);

      if (response.status === 204) {
        return { success: true };
      } else {
        console.error('User creation failed:', response.status, response.statusText);
        return { success: false, error: await response.text() };
      }
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'An error occurred during sign up' };
    }
  };

  useEffect(() => {
    // The wallet is World of Racing's: another brand has no credits to show.
    if (!user?.id || !currentTenant.features.credits) {
      setBalance(null);
      return;
    }
    identityApi.getUserBalance(user.id)
      .then(result => setBalance(result ?? 0))
      .catch(() => setBalance(0));
  }, [user?.id]);

  const refreshBalance = async (): Promise<void> => {
    if (!user?.id || !currentTenant.features.credits) return;
    try {
      const result = await identityApi.getUserBalance(user.id);
      setBalance(result ?? 0);
    } catch {
      // keep existing balance on error
    }
  };

  const logout = () => {
    setUser(null);
    setBalance(null);
    localStorage.removeItem('driverUser');
    localStorage.removeItem('authToken');
  };

  const closeSessionExpiredDialog = () => {
    setIsSessionExpired(false);
  };

  const resetPassword = async (email: string): Promise<boolean> => {
    try {
      // Identity links reset emails to World of Racing's dashboard unless told otherwise, and brands
      // the email from this URL's host. World of Racing keeps sending nothing, exactly as before.
      const response = currentTenant.key === 'wor'
        ? await identityApi.resetPassword(email)
        : await identityApi.resetPassword(email, `${window.location.origin}/`);
      if (response.ok) {
        return true;
      } else {
        console.error('Password reset failed:', response.status, response.statusText);
        return false;
      }
    }
    catch (error) {
      console.error('Password reset error:', error);
      return false;
    }
  };

  async function setPassword(payload: SetPasswordRequestBody): Promise<boolean> {
    const token = await identityApi.setPassword(payload);
    if (!token) {
      return false;
    }
    return await refreshUserProfile();
  }

  async function refreshUserProfile(): Promise<boolean> {
    const user = await identityApi.getUserProfile();
    if (!user) {
      console.error('Failed to fetch user profile after login');
      return false;
    }

    setUser(user);
    localStorage.setItem('driverUser', JSON.stringify(user));
    return true;
  }

  const value: AuthContextType = {
    user,
    balance,
    login,
    signUp,
    logout,
    refreshUserProfile,
    refreshBalance,
    resetPassword,
    setPassword,
    isLoading,
    isSessionExpired,
    closeSessionExpiredDialog
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

